import React from 'react'
import { cn } from '../../lib/utils'

/**
 * Base Skeleton component with shimmer animation
 * Used as building block for skeleton loading states
 */
export const Skeleton = ({ className, ...props }) => {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md bg-gray-200 dark:bg-gray-700',
        // Shimmer effect using gradient
        'before:absolute before:inset-0',
        'before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent',
        'before:animate-skeleton',
        className
      )}
      {...props}
    />
  )
}

/**
 * Skeleton for queue items
 * Matches the QueueItemCard layout
 */
export const SkeletonQueueItem = ({ isDarkMode = false }) => {
  return (
    <div
      className={cn(
        'relative rounded-xl overflow-hidden',
        'p-4',
        isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100/80'
      )}
    >
      <div className="flex items-center gap-3">
        {/* Drag handle skeleton */}
        <Skeleton className="w-6 h-8 rounded" />

        {/* Thumbnail skeleton */}
        <Skeleton className="w-12 h-12 rounded-lg flex-shrink-0" />

        {/* Content skeleton */}
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton className="h-4 w-3/4 rounded" />
          <Skeleton className="h-3 w-1/2 rounded" />
        </div>

        {/* Action button skeleton */}
        <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
      </div>
    </div>
  )
}

/**
 * Skeleton for search result cards
 * Matches the DatabaseSearchCard/YouTubeSearchCard layout
 */
export const SkeletonSearchCard = ({ isDarkMode = false }) => {
  return (
    <div
      className={cn(
        'relative rounded-xl overflow-hidden',
        'p-3',
        isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100/80'
      )}
    >
      <div className="flex items-center gap-3">
        {/* Thumbnail skeleton */}
        <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />

        {/* Content skeleton */}
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton className="h-4 w-full rounded" />
          <Skeleton className="h-3 w-2/3 rounded" />
          <Skeleton className="h-3 w-1/3 rounded" />
        </div>

        {/* Add button skeleton */}
        <Skeleton className="w-10 h-10 rounded-full flex-shrink-0" />
      </div>
    </div>
  )
}

/**
 * Skeleton list - renders multiple skeleton items
 */
export const SkeletonList = ({
  count = 3,
  component: Component = SkeletonQueueItem,
  gap = 'gap-3',
  isDarkMode = false
}) => {
  return (
    <div className={cn('flex flex-col', gap)}>
      {Array.from({ length: count }).map((_, index) => (
        <Component key={index} isDarkMode={isDarkMode} />
      ))}
    </div>
  )
}

export default Skeleton
